import React from 'react';
import { FiMail, FiPhone, FiMapPin } from 'react-icons/fi';
import { AiOutlineInstagram, AiOutlineWhatsApp } from 'react-icons/ai';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useDarkMode } from '../context/DarkModeContext';
import { motion } from 'framer-motion';

const Footer = () => {
  const { isDark } = useDarkMode();
  const currentYear = new Date().getFullYear();

  const navigate = useNavigate();
  const location = useLocation();

  const scrollToSection = (sectionId) => {
    // If we are not on the home page, navigate there first, then scroll
    if (location.pathname !== '/') {
      navigate('/');
      // small delay to allow Home to mount
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        element?.scrollIntoView({ behavior: 'smooth' });
      }, 120);
    } else {
      const element = document.getElementById(sectionId);
      element?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const quickLinks = [
    { label: 'Home', id: 'home' },
    { label: 'About', id: 'about' },
    { label: 'Services', id: 'services' },
    { label: 'Portfolio', id: 'portfolio' },
    { label: 'Contact', id: 'contact' },
  ];

  const contactDetails = [
    {
      icon: FiPhone,
      label: 'Phone',
      value: '03047259307',
      href: 'tel:03047259307',
    },
    {
      icon: FiMail,
      label: 'Email',
      value: 'ahtashamspeaks@gmail.com',
      href: 'mailto:ahtashamspeaks@gmail.com',
    },
    {
      icon: FiMapPin,
      label: 'Location',
      value: 'Lahore, Pakistan',
      href: null,
    },
  ];

  const socialLinks = [
    {
      icon: AiOutlineInstagram,
      href: 'https://www.instagram.com/mediasociety.agency?igsh=Z282bjA2ZWEyOHg5&utm_source=qr',
      label: 'Instagram',
    },
    {
      icon: AiOutlineWhatsApp,
      href: 'https://wa.me/923019681065',
      label: 'WhatsApp',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] },
    },
  };

  const linkVariants = {
    hidden: { opacity: 0, x: -10 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.5 },
    },
  };

  return (
    <footer className={`relative w-full border-t transition-colors duration-500 ${
      isDark
        ? 'bg-dark-primary border-primary-gold/20'
        : 'bg-light-secondary border-primary-gold/30'
    }`}>
      {/* Background Decorations */}
      <motion.div
        className={`absolute top-20 left-0 w-72 h-72 rounded-full blur-3xl -z-10 ${
          isDark ? 'bg-primary-gold/5' : 'bg-primary-gold/8'
        }`}
        animate={{
          scale: [1, 1.1, 1],
          opacity: [0.2, 0.4, 0.2],
        }}
        transition={{ duration: 8, repeat: Infinity }}
      ></motion.div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, margin: '-100px' }}
        >
          {/* Brand Column */}
          <motion.div
            className="space-y-4"
            variants={itemVariants}
          >
            <motion.div
              className="flex items-center gap-1"
              whileHover={{ scale: 1.05 }}
            >
              <span className={`font-montserrat font-bold text-2xl transition-colors ${isDark ? 'text-white' : 'text-black'}`}>
                Media
              </span>
              <span className="font-montserrat font-bold text-2xl text-primary-gold">
                Society
              </span>
            </motion.div>
            <motion.p
              className={`font-poppins text-sm leading-relaxed transition-colors ${isDark ? 'text-gray-400' : 'text-gray-600'}`}
              animate={{ opacity: [0.8, 1, 0.8] }}
              transition={{ duration: 4, repeat: Infinity }}
            >
              Premium social media agency specializing in growth, influence, and conversion.
            </motion.p>
            <motion.div
              className="flex gap-3 pt-4"
              variants={containerVariants}
            >
              {socialLinks.map((social, idx) => {
                const IconComponent = social.icon;
                return (
                  <motion.a
                    key={idx}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-10 h-10 rounded-full border flex items-center justify-center text-primary-gold transition-all duration-300 ${
                      isDark
                        ? 'bg-primary-gold/10 border-primary-gold/20 hover:bg-primary-gold hover:text-black hover:shadow-gold-glow'
                        : 'bg-primary-gold/15 border-primary-gold/30 hover:bg-primary-gold hover:text-black hover:shadow-lg'
                    }`}
                    whileHover={{ scale: 1.2, rotate: 10, y: -5 }}
                    whileTap={{ scale: 0.9 }}
                    animate={{
                      y: [0, -2, 0],
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      delay: idx * 0.2,
                    }}
                    title={social.label}
                    variants={linkVariants}
                  >
                    <IconComponent className="w-5 h-5" />
                  </motion.a>
                );
              })}
            </motion.div>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            variants={itemVariants}
          >
            <motion.h4
              className={`font-montserrat font-bold mb-6 transition-colors ${isDark ? 'text-white' : 'text-black'}`}
              whileHover={{ x: 5 }}
            >
              Quick Links
            </motion.h4>
            <motion.ul
              className="space-y-3"
              variants={containerVariants}
            >
              {quickLinks.map((link, idx) => (
                <motion.li
                  key={link.id}
                  variants={linkVariants}
                  custom={idx}
                >
                  <motion.button
                    onClick={() => scrollToSection(link.id)}
                    className={`font-poppins transition-all bg-none border-none cursor-pointer ${
                      isDark
                        ? 'text-gray-400 hover:text-primary-gold'
                        : 'text-gray-600 hover:text-primary-gold'
                    }`}
                    whileHover={{ x: 8, scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {link.label}
                  </motion.button>
                </motion.li>
              ))}
            </motion.ul>
          </motion.div>

          {/* Services */}
          <motion.div
            variants={itemVariants}
          >
            <motion.h4
              className={`font-montserrat font-bold mb-6 transition-colors ${isDark ? 'text-white' : 'text-black'}`}
              whileHover={{ x: 5 }}
            >
              Services
            </motion.h4>
            <motion.ul
              className="space-y-3"
              variants={containerVariants}
            >
              {[
                'Strategic Growth',
                'Influence & Authority',
                'Smart Strategy',
                'Content Creation',
                'Analytics & Reporting',
              ].map((service, idx) => (
                <motion.li
                  key={idx}
                  variants={linkVariants}
                  custom={idx}
                >
                  <motion.button
                    onClick={() => scrollToSection('services')}
                    className={`font-poppins transition-all bg-none border-none cursor-pointer ${
                      isDark
                        ? 'text-gray-400 hover:text-primary-gold'
                        : 'text-gray-600 hover:text-primary-gold'
                    }`}
                    whileHover={{ x: 8, scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {service}
                  </motion.button>
                </motion.li>
              ))}
            </motion.ul>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            variants={itemVariants}
          >
            <motion.h4
              className={`font-montserrat font-bold mb-6 transition-colors ${isDark ? 'text-white' : 'text-black'}`}
              whileHover={{ x: 5 }}
            >
              Contact Us
            </motion.h4>
            <motion.ul
              className="space-y-4"
              variants={containerVariants}
            >
              {contactDetails.map((detail, idx) => {
                const IconComponent = detail.icon;
                return (
                  <motion.li
                    key={idx}
                    className="flex items-center gap-3"
                    variants={linkVariants}
                    custom={idx}
                    whileHover={{ x: 6 }}
                  >
                    <motion.div
                      animate={{ rotate: [0, 10, 0] }}
                      transition={{ duration: 3, repeat: Infinity, delay: idx * 0.3 }}
                    >
                      <IconComponent className="w-5 h-5 text-primary-gold flex-shrink-0" />
                    </motion.div>
                    {detail.href ? (
                      <motion.a
                        href={detail.href}
                        className={`font-poppins text-sm transition-colors ${
                          isDark
                            ? 'text-gray-400 hover:text-primary-gold'
                            : 'text-gray-600 hover:text-primary-gold'
                        }`}
                        whileHover={{ scale: 1.05 }}
                      >
                        {detail.value}
                      </motion.a>
                    ) : (
                      <p className={`font-poppins text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        {detail.value}
                      </p>
                    )}
                  </motion.li>
                );
              })}
            </motion.ul>
          </motion.div>
        </motion.div>

        {/* Divider */}
        <motion.div
          className={`border-t my-8 ${isDark ? 'border-primary-gold/10' : 'border-primary-gold/20'}`}
          initial={{ scaleX: 0 }}
          whileInView={{ scaleX: 1 }}
          viewport={{ once: false }}
          transition={{ duration: 0.8 }}
        ></motion.div>

        {/* Bottom Section */}
        <motion.div
          className="flex flex-col md:flex-row justify-between items-center gap-4"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, margin: '-50px' }}
        >
          <motion.p
            className={`font-poppins text-sm text-center md:text-left transition-colors ${isDark ? 'text-gray-500' : 'text-gray-600'}`}
            variants={itemVariants}
            animate={{ opacity: [0.7, 1, 0.7] }}
            transition={{ duration: 3, repeat: Infinity }}
          >
            © {currentYear} Media Society. All rights reserved.
          </motion.p>

          {/* Legal Links */}
          <motion.div
            className="flex gap-6 font-poppins text-sm flex-wrap justify-center md:justify-start"
            variants={containerVariants}
          >
            <Link to="/privacy-policy">
              <motion.button
                className={`transition-all bg-none border-none cursor-pointer ${
                  isDark
                    ? 'text-gray-500 hover:text-primary-gold'
                    : 'text-gray-600 hover:text-primary-gold'
                }`}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                variants={linkVariants}
              >
                Privacy Policy
              </motion.button>
            </Link>
            <Link to="/terms-and-conditions">
              <motion.button
                className={`transition-all bg-none border-none cursor-pointer ${
                  isDark
                    ? 'text-gray-500 hover:text-primary-gold'
                    : 'text-gray-600 hover:text-primary-gold'
                }`}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                variants={linkVariants}
              >
                Terms of Service
              </motion.button>
            </Link>
            <Link to="/cookies-policy">
              <motion.button
                className={`transition-all bg-none border-none cursor-pointer ${
                  isDark
                    ? 'text-gray-500 hover:text-primary-gold'
                    : 'text-gray-600 hover:text-primary-gold'
                }`}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                variants={linkVariants}
              >
                Cookies Policy
              </motion.button>
            </Link>
          </motion.div>
        </motion.div>

        {/* Accent Line */}
        <motion.div
          className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-primary-gold to-transparent"
          animate={{ opacity: [0.1, 0.3, 0.1] }}
          transition={{ duration: 4, repeat: Infinity }}
        ></motion.div>
      </div>
    </footer>
  );
};

export default Footer;
